import { useEffect, useMemo } from 'react'

import LoadingSpinnerCentered from 'components/reusable/LoadingSpinnerCentered'
import {
  Button,
  Checkbox,
  FilterPillButton,
  FiltersDropdown
} from 'slab'
import { type GetMemberOnboardingEnrollmentEventsEnrollmentEventIdMedicalPlansMetalTierItem as MetalTier, type GetMemberOnboardingEnrollmentEventsEnrollmentEventIdMedicalPlansPlanTypeItem as PlanType } from 'utils/web_api.schemas'

import { sortMetalTiers } from '../utils'

export interface PlanGranularFiltersTypes {
  metalTiers: MetalTier[];
  carriers: string[];
  planTypes: PlanType[];
}

interface PlanGranularFiltersProps {
  carrierFilterNames?: string[];
  metalTierFilterNames?: MetalTier[];
  planTypeFilterNames?: PlanType[];
  filters: PlanGranularFiltersTypes;
  setFilters: (filters: PlanGranularFiltersTypes) => void;
}

export function PlanGranularFilters ({
  carrierFilterNames,
  metalTierFilterNames,
  planTypeFilterNames,
  filters,
  setFilters,
}: PlanGranularFiltersProps) {
  // Sort metal tier dropdown as platinum > gold > silver ... on initial render only
  const sortedMetalTierFilterNames = useMemo(() => {
    if (!metalTierFilterNames) return undefined
    return sortMetalTiers(metalTierFilterNames)
  }, [metalTierFilterNames])

  // Update URL when filters change
  useEffect(() => {
    const pageURL = new URL(window.location.href)

    if (filters.metalTiers.length > 0) {
      pageURL.searchParams.set('metal_tiers', filters.metalTiers.join(','))
    } else {
      pageURL.searchParams.delete('metal_tiers')
    }

    if (filters.carriers.length > 0) {
      pageURL.searchParams.set('carriers', filters.carriers.join(','))
    } else {
      pageURL.searchParams.delete('carriers')
    }

    if (filters.planTypes.length > 0) {
      pageURL.searchParams.set('plan_types', filters.planTypes.join(','))
    } else {
      pageURL.searchParams.delete('plan_types')
    }

    // Update URL without reloading the page
    window.history.pushState({}, '', pageURL.toString())
  }, [filters])

  const toggle = (
    filterType: keyof PlanGranularFiltersTypes,
    filter: MetalTier | PlanType | string
  ) => {
    return () => {
      const newFilters = { ...filters }

      // Expanding these operations for type safety
      if (filterType === 'metalTiers') {
        const metalTier = filter as MetalTier
        newFilters.metalTiers = filters.metalTiers.includes(metalTier)
          ? filters.metalTiers.filter((f) => f !== metalTier)
          : [...filters.metalTiers, metalTier]
      } else if (filterType === 'planTypes') {
        const planType = filter as PlanType
        newFilters.planTypes = filters.planTypes.includes(planType)
          ? filters.planTypes.filter((f) => f !== planType)
          : [...filters.planTypes, planType]
      } else if (filterType === 'carriers') {
        const carrier = filter as string
        newFilters.carriers = filters.carriers.includes(carrier)
          ? filters.carriers.filter((f) => f !== carrier)
          : [...filters.carriers, carrier]
      }

      setFilters(newFilters)
    }
  }

  const onClearFilters = () => {
    setFilters({
      metalTiers: [],
      carriers: [],
      planTypes: [],
    })
  }

  const renderMetalTierFilters = () => {
    const loading = !sortedMetalTierFilterNames || sortedMetalTierFilterNames.length === 0

    if (loading) {
      return <div><LoadingSpinnerCentered height='1.5rem' width='1.5rem' /></div>
    }

    return sortedMetalTierFilterNames.map((tier) => (
      <Checkbox
        key={tier}
        checked={filters.metalTiers.includes(tier)}
        onChange={toggle('metalTiers', tier)}
        value={tier}
        label={
          tier === 'expanded_bronze'
            ? 'Expanded bronze'
            : tier.charAt(0).toUpperCase() + tier.slice(1)
        }
      />
    ))
  }

  const renderCarrierFilters = () => {
    const loading = !carrierFilterNames || carrierFilterNames.length === 0

    if (loading) {
      return <div><LoadingSpinnerCentered height='1.5rem' width='1.5rem' /></div>
    }

    return carrierFilterNames?.map((carrier) => (
      <Checkbox
        key={carrier}
        checked={filters.carriers.includes(carrier)}
        onChange={toggle('carriers', carrier)}
        value={carrier}
        label={carrier}
      />
    ))
  }

  const renderPlanTypeFilters = () => {
    const loading = !planTypeFilterNames || planTypeFilterNames.length === 0

    if (loading) {
      return <div><LoadingSpinnerCentered height='1.5rem' width='1.5rem' /></div>
    }

    return planTypeFilterNames?.map((planType) => (
      <Checkbox
        key={planType}
        checked={filters.planTypes.includes(planType)}
        onChange={toggle('planTypes', planType)}
        value={planType}
        label={planType.toUpperCase()}
      />
    ))
  }

  return (
    <div className='flex justify-start gap-2 mb-6 flex-nowrap overflow-auto'>
      <FiltersDropdown
        trigger={
          <FilterPillButton
            label='Metal tier'
            count={filters.metalTiers.length}
          />
        }
      >
        <FiltersDropdown.Filters>
          {renderMetalTierFilters()}
        </FiltersDropdown.Filters>
      </FiltersDropdown>
      <FiltersDropdown
        trigger={
          <FilterPillButton
            label='Insurance company'
            count={filters.carriers.length}
          />
        }
      >
        <FiltersDropdown.Filters>
          {renderCarrierFilters()}
        </FiltersDropdown.Filters>
      </FiltersDropdown>
      <FiltersDropdown
        trigger={
          <FilterPillButton
            label='Plan type'
            count={filters.planTypes.length}
          />
        }
      >
        <FiltersDropdown.Filters>
          {renderPlanTypeFilters()}
        </FiltersDropdown.Filters>
      </FiltersDropdown>
      {(filters.metalTiers.length > 0 ||
        filters.carriers.length > 0 ||
        filters.planTypes.length > 0) && (
          <Button size='small' variant='tertiary-deemphasized' onClick={() => onClearFilters()}>
            Clear filters
          </Button>
      )}
    </div>
  )
}
